import { MouseEventHandler, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@mui/material';

import { getMinSharesToBuy } from '@arrived/common';
import { useIsOfferingBlocked } from '@arrived/hooks';
import { Offering, OfferingStatus } from '@arrived/models';
import {
  useCanUserTransactEarlyAccess,
  useSharesAvailableForOfferingQuery,
  useSharesAvailableQuery,
} from '@arrived/queries';

import { NotifyMeButtonContext } from '../NotifyMeButtonContext';
import { InvestorsCount } from './InvestorsCount';
import { PropertyGridCardContentTypography } from './PropertyGridCardContentTypography';

const NotifyMeButton = () => {
  const onClick = useContext(NotifyMeButtonContext);

  const handleOnClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    },
    [onClick]
  );

  return (
    <Button size="small" variant="outlined" onClick={handleOnClick}>
      <FormattedMessage id="common.notify.me" />
    </Button>
  );
};

const investNowButtonId = 'invest-now-button';

interface InvestNowButtonProps {
  offering: Offering;
  onInvestNowClick: (content?: string) => void;
}

const InvestNowButton = ({ offering, onInvestNowClick }: InvestNowButtonProps) => {
  const sharesAvailableState = useSharesAvailableQuery(offering.id);
  const sharesAvailableForOfferingState = useSharesAvailableForOfferingQuery(offering.id);

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const text = document.getElementById(investNowButtonId)?.textContent ?? undefined;
      onInvestNowClick(text);
    },
    [onInvestNowClick]
  );

  const isSoldOut = useMemo(
    () =>
      sharesAvailableForOfferingState.isSuccess &&
      sharesAvailableForOfferingState.data.availableSharesCount < getMinSharesToBuy(offering),
    [offering, sharesAvailableState]
  );
  const isMaxedOut = useMemo(
    () =>
      sharesAvailableState.isSuccess && sharesAvailableState.data.availableSharesCount < getMinSharesToBuy(offering),
    [offering, sharesAvailableState]
  );

  return (
    <>
      {isSoldOut ? (
        <NoActions investorsCount={offering.investorsCount} />
      ) : (
        <Button disabled={isMaxedOut} fullWidth={false} id={investNowButtonId} size="small" onClick={onClick}>
          {isMaxedOut ? <FormattedMessage id="common.maxed.out" /> : <FormattedMessage id="common.invest.now" />}
        </Button>
      )}
    </>
  );
};

export interface PropertyGridCardRightContentProps {
  offering: Offering;
  onInvestNowClick: (content?: string) => void;
}

const NoActions = ({ investorsCount }: { investorsCount: number }) => (
  <PropertyGridCardContentTypography color={({ palette }) => palette.darkened.steel}>
    <InvestorsCount investorsCount={investorsCount} />
  </PropertyGridCardContentTypography>
);

export const PropertyGridCardRightContent = ({ offering, onInvestNowClick }: PropertyGridCardRightContentProps) => {
  const canUserTransactEarlyAccess = useCanUserTransactEarlyAccess();
  const isOfferingBlocked = useIsOfferingBlocked(offering.shortName);

  if (isOfferingBlocked) {
    return null;
  }

  switch (offering.status) {
    case OfferingStatus.PRE_LAUNCH:
    case OfferingStatus.COMING_SOON:
      return <NotifyMeButton />;
    case OfferingStatus.TRANSACT_EARLY_ACCESS:
      if (canUserTransactEarlyAccess) {
        return <InvestNowButton offering={offering} onInvestNowClick={onInvestNowClick} />;
      } else {
        return <NotifyMeButton />;
      }
    case OfferingStatus.TRANSACT_ALL:
      return <InvestNowButton offering={offering} onInvestNowClick={onInvestNowClick} />;
    case OfferingStatus.PAUSED:
      return null;
    default:
      return <NoActions investorsCount={offering.investorsCount} />;
  }
};
